/* GreenOx: one reachable desktop menu row, stable hover and viewport-safe panels. */
(() => {
  if (window.gxHeaderNavigationLoaded) return;
  window.gxHeaderNavigationLoaded = true;
  const instances = new WeakMap();

  function enhance(header) {
    if (instances.has(header)) return;
    const nav = header.querySelector(':scope > .header__inline-menu');
    if (!nav || header.classList.contains('drawer-menu')) return;
    const shell = header.closest('.header-wrapper');
    const drawer = header.querySelector('header-drawer');
    const drawerBreakpoint = drawer?.dataset.breakpoint;
    header.classList.add('gx-nav-ready');
    const list = nav.querySelector(':scope > .list-menu--inline');
    const items = Array.from(list.children);
    const menus = Array.from(nav.querySelectorAll('header-menu > details'));
    const desktop = window.matchMedia('(min-width: 990px)');
    const hover = window.matchMedia('(hover: hover) and (pointer: fine)');
    const controller = new AbortController();
    const signal = controller.signal;
    let timer;
    let frame;
    let destroyed = false;
    const listen = (node, type, callback, options = {}) =>
      node.addEventListener(type, callback, { ...options, signal });
    const cancel = () => window.clearTimeout(timer);

    function sync() {
      shell.preventHide = menus.some((menu) => menu.open);
      menus.forEach((menu) => {
        menu.querySelector('summary').setAttribute('aria-expanded', String(menu.open));
      });
    }

    function close(except) {
      menus.forEach((menu) => {
        if (menu === except) return;
        menu.open = false;
        menu.querySelectorAll('details[open]').forEach((nested) => {
          nested.open = false;
          nested.querySelector('summary')?.setAttribute('aria-expanded', 'false');
        });
      });
      sync();
    }

    function fitPanels() {
      const viewport = window.visualViewport;
      const bottom = viewport ? viewport.offsetTop + viewport.height : window.innerHeight;
      menus.forEach((menu) => {
        if (!menu.open) return;
        const panel = menu.querySelector('summary').nextElementSibling;
        const available = Math.max(0, Math.floor(bottom - panel.getBoundingClientRect().top - 12));
        panel.style.setProperty('--gx-menu-available-height', `${available}px`);
      });
    }

    function open(menu) {
      cancel();
      close(menu);
      menu.open = true;
      sync();
      fitPanels();
    }

    function setDrawerMode(useDrawer) {
      if (!drawer) return;
      const breakpoint = useDrawer ? 'desktop' : drawerBreakpoint;
      const summary = drawer.querySelector('details[open] > summary');
      if (summary && (drawer.dataset.breakpoint !== breakpoint || (desktop.matches && !useDrawer))) {
        drawer.closeMenuDrawer?.(new Event('resize'), summary);
        summary.setAttribute('aria-expanded', 'false');
      }
      drawer.dataset.breakpoint = breakpoint;
    }

    function layout() {
      frame = null;
      if (destroyed || !header.isConnected) return;
      if (!desktop.matches) {
        header.classList.remove('gx-nav-below', 'gx-nav-drawer');
        close();
        setDrawerMode(false);
        return;
      }
      // Reveal only for synchronous measurement if the overflow fallback is active.
      header.classList.remove('gx-nav-drawer');
      const style = getComputedStyle(header);
      const width = header.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
      const gap = parseFloat(style.columnGap) || 0;
      const itemGap = parseFloat(getComputedStyle(list).columnGap) || 0;
      const required = items.reduce((sum, item) => sum + item.getBoundingClientRect().width, 0)
        + Math.max(0, items.length - 1) * itemGap;
      const logo = header.querySelector('.gx-header-logo')?.getBoundingClientRect().width || 0;
      const icons = header.querySelector('.header__icons');
      const iconStyle = icons ? getComputedStyle(icons) : null;
      const iconChildren = icons ? Array.from(icons.children).filter((node) => node.getBoundingClientRect().width) : [];
      const iconWidth = iconChildren.reduce((sum, node) => sum + node.getBoundingClientRect().width, 0)
        + Math.max(0, iconChildren.length - 1) * (parseFloat(iconStyle?.columnGap) || 0);
      const centered = header.classList.contains('header--middle-center');
      const middle = centered || header.classList.contains('header--middle-left');
      const inlineWidth = centered ? (width - logo - 2 * gap) / 2 : width - logo - iconWidth - 2 * gap;
      const useDrawer = required > width - 2;
      header.classList.toggle('gx-nav-below', !useDrawer && middle && required > inlineWidth - 2);
      header.classList.toggle('gx-nav-drawer', useDrawer);
      if (useDrawer) close();
      setDrawerMode(useDrawer);
      document.documentElement.style.setProperty('--header-height', `${shell.offsetHeight}px`);
      fitPanels();
    }

    function scheduleLayout() {
      if (frame == null) frame = requestAnimationFrame(layout);
    }

    items.forEach((item) => {
      const menu = item.querySelector('header-menu > details');
      const trigger = menu ? menu.querySelector('summary') : item.querySelector('a');
      if (!trigger) return;
      listen(trigger, 'pointerenter', (event) => {
        if (event.pointerType !== 'mouse' || !hover.matches || !desktop.matches) return;
        cancel();
        // A brief delay avoids switching menus while moving diagonally to a panel.
        timer = window.setTimeout(() => menu ? open(menu) : close(), 140);
      });
      listen(trigger, 'pointerleave', cancel);
      if (!menu) return;
      const panel = trigger.nextElementSibling;
      listen(panel, 'pointerenter', cancel);
      listen(menu, 'toggle', () => {
        if (menu.open) close(menu);
        sync();
        fitPanels();
      });
      panel.querySelectorAll('details > summary').forEach((nestedSummary) => {
        listen(nestedSummary, 'pointerenter', (event) => {
          if (event.pointerType !== 'mouse' || !hover.matches) return;
          cancel();
          timer = window.setTimeout(() => {
            nestedSummary.parentElement.open = true;
            nestedSummary.setAttribute('aria-expanded', 'true');
            fitPanels();
          }, 140);
        });
        listen(nestedSummary, 'pointerleave', cancel);
      });
      listen(trigger, 'click', cancel); // Native details toggling supports touch and keyboard.
    });

    listen(shell, 'pointerenter', cancel);
    listen(shell, 'pointerleave', (event) => {
      if (event.pointerType !== 'mouse' || !hover.matches) return;
      cancel();
      timer = window.setTimeout(() => {
        if (!nav.contains(document.activeElement)) close();
      }, 240);
    });
    listen(document, 'pointerdown', (event) => {
      if (!shell.contains(event.target)) { cancel(); close(); }
    });
    listen(nav, 'keydown', (event) => {
      if (event.key !== 'Escape') return;
      const active = menus.find((menu) => menu.open);
      if (!active) return;
      event.preventDefault();
      cancel();
      close();
      active.querySelector('summary').focus();
    });
    listen(window, 'resize', scheduleLayout);
    listen(window, 'scroll', fitPanels, { passive: true });
    if (window.visualViewport) {
      listen(window.visualViewport, 'resize', scheduleLayout);
      listen(window.visualViewport, 'scroll', fitPanels, { passive: true });
    }
    const observer = new ResizeObserver(scheduleLayout);
    observer.observe(header);
    items.forEach((item) => observer.observe(item));
    document.fonts?.ready.then(scheduleLayout);
    instances.set(header, () => {
      destroyed = true;
      cancel();
      cancelAnimationFrame(frame);
      observer.disconnect();
      controller.abort();
      instances.delete(header);
    });
    layout();
  }

  const initialize = (root = document) => root.querySelectorAll('header[id^="GxHeader-"]').forEach(enhance);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  } else {
    initialize();
  }
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('header[id^="GxHeader-"]').forEach((header) => instances.get(header)?.());
  });
})();
